import { type ICurrency, Locales, usePrevioCore } from '@previo/core'
import { Codebook } from '../Codebook'
import { $t } from '../../composables/i18n'
import { CurrencyFlag } from './CurrencyFlag'

const { useLocale } = usePrevioCore()

export enum CurrencyId {
  CZK = 1,
  EUR = 2,
  USD = 3,
  SKK = 4,
  GBP = 5,
  PLN = 6,
  RUB = 7,
  UAH = 8,
  HUF = 9,
  DKK = 10,
  CRC = 12,
  RSD = 14,
  DOP = 15,
  HNL = 16,
  HRK = 17,
  CHF = 18,
  JPY = 19,
  AED = 20,
  CNY = 21,
  RON = 22,
  THB = 23,
  SCR = 24,
  BGN = 25,
  IDR = 26,
  LKR = 27,
  AUD = 28,
}

export enum CurrencyCode {
  CZK = 'CZK',
  EUR = 'EUR',
  USD = 'USD',
  SKK = 'SKK',
  GBP = 'GBP',
  PLN = 'PLN',
  RUB = 'RUB',
  UAH = 'UAH',
  HUF = 'HUF',
  DKK = 'DKK',
  CRC = 'CRC',
  RSD = 'RSD',
  DOP = 'DOP',
  HNL = 'HNL',
  HRK = 'HRK',
  CHF = 'CHF',
  JPY = 'JPY',
  AED = 'AED',
  CNY = 'CNY',
  RON = 'RON',
  THB = 'THB',
  SCR = 'SCR',
  BGN = 'BGN',
  IDR = 'IDR',
  LKR = 'LKR',
  AUD = 'AUD',
}

export class Currency extends Codebook<CurrencyId> implements ICurrency {
  static readonly CZK = new Currency(
    CurrencyId.CZK,
    CurrencyCode.CZK,
    CurrencyFlag.CZK,
    () => $t('Czech koruna'),
    1,
  )
  static readonly EUR = new Currency(
    CurrencyId.EUR,
    CurrencyCode.EUR,
    CurrencyFlag.EUR,
    () => $t('Euro'),
    0.037418,
  )
  static readonly USD = new Currency(
    CurrencyId.USD,
    CurrencyCode.USD,
    CurrencyFlag.USD,
    () => $t('American dollar'),
    0.044277,
  )
  static readonly SKK = new Currency(
    CurrencyId.SKK,
    CurrencyCode.SKK,
    CurrencyFlag.SKK,
    () => $t('Slovak koruna'),
    0,
  )
  static readonly GBP = new Currency(
    CurrencyId.GBP,
    CurrencyCode.GBP,
    CurrencyFlag.GBP,
    () => $t('British pound'),
    0.034168,
  )
  static readonly PLN = new Currency(
    CurrencyId.PLN,
    CurrencyCode.PLN,
    CurrencyFlag.PLN,
    () => $t('Polish złoty'),
    0.166889,
  )
  static readonly RUB = new Currency(
    CurrencyId.RUB,
    CurrencyCode.RUB,
    CurrencyFlag.RUB,
    () => $t('Russian ruble'),
    3.336,
  )
  static readonly UAH = new Currency(
    CurrencyId.UAH,
    CurrencyCode.UAH,
    CurrencyFlag.UAH,
    () => $t('Ukrainian hryvnia'),
    1.248,
  )
  static readonly HUF = new Currency(
    CurrencyId.HUF,
    CurrencyCode.HUF,
    CurrencyFlag.HUF,
    () => $t('Hungarian forint'),
    13.499,
  )
  static readonly DKK = new Currency(
    CurrencyId.DKK,
    CurrencyCode.DKK,
    CurrencyFlag.DKK,
    () => $t('Danish krone'),
    0.278396,
  )
  static readonly CRC = new Currency(
    CurrencyId.CRC,
    CurrencyCode.CRC,
    CurrencyFlag.CRC,
    () => $t('Costa Rican colón'),
    26.38,
  )
  static readonly RSD = new Currency(
    CurrencyId.RSD,
    CurrencyCode.RSD,
    CurrencyFlag.RSD,
    () => $t('Serbian dinar'),
    4.396,
  )
  static readonly DOP = new Currency(
    CurrencyId.DOP,
    CurrencyCode.DOP,
    CurrencyFlag.DOP,
    () => $t('Dominican peso'),
    2.59,
  )
  static readonly HNL = new Currency(
    CurrencyId.HNL,
    CurrencyCode.HNL,
    CurrencyFlag.HNL,
    () => $t('Honduran lempira'),
    1.084,
  )
  static readonly HRK = new Currency(
    CurrencyId.HRK,
    CurrencyCode.HRK,
    CurrencyFlag.HRK,
    () => $t('Croatian kuna'),
    0.282247,
  )
  static readonly CHF = new Currency(
    CurrencyId.CHF,
    CurrencyCode.CHF,
    CurrencyFlag.CHF,
    () => $t('Swiss franc'),
    0.040319,
  )
  static readonly JPY = new Currency(
    CurrencyId.JPY,
    CurrencyCode.JPY,
    CurrencyFlag.JPY,
    () => $t('Japanese yen'),
    4.62,
  )
  static readonly AED = new Currency(
    CurrencyId.AED,
    CurrencyCode.AED,
    CurrencyFlag.AED,
    () => $t('Dirham'),
    0.161684,
  )
  static readonly CNY = new Currency(
    CurrencyId.CNY,
    CurrencyCode.CNY,
    CurrencyFlag.CNY,
    () => $t('Chinese yuan'),
    0.300391,
  )
  static readonly RON = new Currency(
    CurrencyId.RON,
    CurrencyCode.RON,
    CurrencyFlag.RON,
    () => $t('Romanian leu'),
    0.181884,
  )
  static readonly THB = new Currency(
    CurrencyId.THB,
    CurrencyCode.THB,
    CurrencyFlag.THB,
    () => $t('Thai baht'),
    1,
  )
  static readonly SCR = new Currency(
    CurrencyId.SCR,
    CurrencyCode.SCR,
    CurrencyFlag.SCR,
    () => $t('Seychellois rupee'),
    0.53,
  )
  static readonly BGN = new Currency(
    CurrencyId.BGN,
    CurrencyCode.BGN,
    CurrencyFlag.BGN,
    () => $t('Bulgarian lev'),
    0.066666,
  )
  static readonly IDR = new Currency(
    CurrencyId.IDR,
    CurrencyCode.IDR,
    CurrencyFlag.IDR,
    () => $t('Indonesian rupee'),
    720,
  )
  static readonly LKR = new Currency(
    CurrencyId.LKR,
    CurrencyCode.LKR,
    CurrencyFlag.LKR,
    () => $t('Sri Lankan rupee'),
    13,
  )
  static readonly AUD = new Currency(
    CurrencyId.AUD,
    CurrencyCode.AUD,
    CurrencyFlag.AUD,
    () => $t('Australian dollar'),
    0.07,
  )

  private constructor(
    public readonly id: CurrencyId,
    public readonly code: CurrencyCode,
    public readonly flag: CurrencyFlag,
    public readonly name: () => string,
    public readonly saldoEpsilon: number,
  ) {
    super(id)
  }

  /**
   * Formats price to locale currency
   */
  public formatPrice = (
    price: number,
    locale: Locales = useLocale().get(),
    precision: number = 2,
  ): string => {
    let currencyReplaced = false

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.code,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
      .formatToParts(price)
      .map((item, idx, arr) => {
        if ((item.type === 'currency' || item.type === 'literal') && currencyReplaced) {
          return ''
        }

        const nextCurrency = arr[idx + 1] && arr[idx + 1].type === 'currency' && arr[idx + 1].value

        if (item.type === 'minusSign' && nextCurrency && !currencyReplaced) {
          currencyReplaced = true
          return `${nextCurrency} ${item.value}`
        }
        return `${item.value}`
      })
      .join('')
  }

  public currencySymbol = (locale: Locales = useLocale().get()): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(0)
      .replace(/\d/g, '')
      .trim()
  }
}
